'use client';

import React, { useEffect, useState } from 'react';
import { PagePayoutSchema } from '@/sanity/pages/PagePayoutTemplate';

import { cn } from '@/lib/utils';
import Image from 'next/image';
import { PortableText } from '@portabletext/react';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { CTAButton } from '@/shadcn/ui/button';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@/shadcn/ui/carousel';

import laurels from '@/public/payout/laurels.svg';
import ipad from '@/public/global/ipad.png';
import { PageAssetsSchema } from '@/sanity/pages/PageAssetsTemplate';
import { motion } from 'framer-motion';
import bulletIcon from '@/public/assets-page/bulletIcon.svg';
const ease = [0.25, 0.1, 0.25, 1] as const;

export default function PlatformSection({
  data,
  className,
}: {
  data: PageAssetsSchema;
  className?: string;
}) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const isBrazil = data?.settings?.slug?.current?.endsWith('/br');

  useEffect(() => {
    if (!api) return;

    setCurrent(api.selectedScrollSnap());

    api.on('select', () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  return (
    <section className={cn('relative overflow-clip', className)}>
      <div className="container px-[2rem] lg:px-0 flex flex-col gap-[1.25rem] items-center py-[8rem] pb-[8rem] lg:pt-[16rem] relative">
        {data?.heroChip && (
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease, delay: 0.2 }}
            className="mb-0 font-blacker italic text-[2rem] text-black leading-[1.5]"
          >
            {data.heroChip}
          </motion.p>
        )}

        <motion.div
          className="richtext w-full prose-headings:w-full
          prose-p:text-black/70 prose-p:text-[2.25rem] lg:prose-p:text-[2rem] prose-p:w-[min(46.5rem,95%)] prose-strong:text-black [text-wrap-style:pretty] flex flex-col gap-[2rem]"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          {data?.platformText && (
            <PortableText
              value={data?.platformText}
              components={richTextComponent}
            />
          )}
        </motion.div>

        {data.platformList && (
          <div className="w-[min(151rem,100%)] mt-[4rem] lg:mt-[6rem] flex flex-col gap-[2rem] lg:gap-[6rem]">
            {data.platformList.map((item, index) => (
              <>
                {item.enable && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-100px' }}
                    transition={{
                      duration: 0.3,
                      ease: [0.25, 0.46, 0.45, 0.94],
                    }}
                    key={'platform' + index}
                    className="bg-white  border border-[#E5E5E5] p-[3rem] flex flex-col lg:flex-row items-center justify-between gap-[3rem]"
                  >
                    <div className="lg:p-[4rem] flex flex-col gap-[3rem] lg:gap-[5rem] w-full">
                      <div className="flex items-center gap-[1.5rem]">
                        <Image
                          src={item.logo}
                          alt={item.name + ' platform logo'}
                          className="!w-[5.5rem] shrink-0"
                        />
                        <span className="text-[3rem] lg:text-[4rem] text-[#103037] font-medium tracking-[-0.09rem] lg:tracking-[-0.12rem]">
                          {item.name}
                        </span>
                      </div>

                      <div className="flex flex-col gap-[2rem] lg:gap-[3.5rem]">
                        {item.bullets.map((bullet, indexB) => (
                          <div
                            key={'plat' + index + 'bull' + indexB}
                            className="flex items-center gap-[1.5rem]"
                          >
                            <Image
                              src={bulletIcon}
                              alt={'bullet'}
                              className="!w-[3rem] shrink-0"
                            />
                            <span className="text-[2.25rem] text-[#103037] font-normal">
                              {bullet}
                            </span>
                          </div>
                        ))}
                      </div>

                      {item.annotation && (
                        <motion.p
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.8, ease, delay: 0.2 }}
                          className="mb-0 font-blacker italic text-[2rem] text-black leading-[1.5]"
                        >
                          {item.annotation}
                        </motion.p>
                      )}
                    </div>

                    <Image
                      src={item.image}
                      alt={'image'}
                      className="!w-full lg:!w-[67.875rem] shrink-0"
                    />
                  </motion.div>
                )}
              </>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
